import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import HamburgerMenu from './global/menu';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import Welcome from './onboarding/welcome';
import Signup from './onboarding/signup';
import EmailConfirmation from './onboarding/email-confirmation';
import Login from './onboarding/login';
import ForgotPassword from './onboarding/forgot-password';
import PasswordReset from './onboarding/password-reset';
import SetPin from './onboarding/set-pin';
import DashboardHome from './dashboard/home';
import Admin from './dashboard/admin';

// Import Dashboard Components
import DailyDashboard from './dashboard/daily';

// Import Admin-only Components
import ChainOfCustodyReport from './dashboard/chainOfCustodyReport';
import ProviderUsageReport from './dashboard/providerUsageReport';
import Users from './dashboard/users';

// Import generic location component and config
import LocationInventory from './locations/LocationInventory';
import { LOCATIONS, BRANDING } from './config';

import { authHelpers, supabase } from './global/supabase';
import './App.css';

// Columns the client may read from public.users (pin_hash is never granted).
const USER_PROFILE_COLUMNS = 'id, email, first_name, last_name, state_license_number, is_admin, is_editor, is_active, profile_completed, pin_reset_required';
const SET_PIN_PATH = '/set-pin';

// Apply theme colors from config to CSS variables
const applyTheme = () => {
  const root = document.documentElement;
  root.style.setProperty('--primary-color', BRANDING.primaryColor);
  root.style.setProperty('--secondary-color', BRANDING.secondaryColor || '#333');
  root.style.setProperty('--background-color', BRANDING.backgroundColor || '#f8f9fa');
  root.style.setProperty('--card-background', BRANDING.cardBackground || '#ffffff');
  root.style.setProperty('--text-color', BRANDING.textColor || '#333');
  root.style.setProperty('--text-muted', BRANDING.textMuted || '#666');
  root.style.setProperty('--border-color', BRANDING.borderColor || '#e0e0e0');
};
applyTheme();

// Profile Component
function Profile({ userInfo, setUserInfo, isAdmin, onLogout, onNavigate }) {
  const navigate = useNavigate();

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [editingData, setEditingData] = useState({
    firstName: '',
    lastName: '',
    stateLicenseNumber: '',
    pin: ''
  });
  const [emailChangeData, setEmailChangeData] = useState({
    newEmail: '',
    isProcessing: false,
    pendingEmail: '',
    status: '',
    message: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    isProcessing: false,
    status: '',
    message: '',
    showCurrentPassword: false,
    showNewPassword: false
  });

  // Update editing data when userInfo changes
  useEffect(() => {
    if (userInfo) {
      setEditingData({
        firstName: userInfo.firstName || '',
        lastName: userInfo.lastName || '',
        stateLicenseNumber: userInfo.stateLicenseNumber || '',
        pin: ''
      });
    }
  }, [userInfo]);

  // Detect mobile device for PWA install button
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    setIsMobile(isIOS || isAndroid);
  }, []);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const { authHelpers } = await import('./global/supabase');

      const newPin = editingData.pin.trim();
      if (newPin && !/^\d{6}$/.test(newPin)) {
        alert('PIN must be exactly 6 digits');
        return;
      }

      // public.users is the source of truth; the PIN never goes into user_metadata.
      const { error: profileError } = await authHelpers.supabase.rpc('update_user_metadata', {
        user_id: userInfo.id,
        first_name: editingData.firstName,
        last_name: editingData.lastName,
        state_license_number: editingData.stateLicenseNumber
      });
      if (profileError) {
        throw profileError;
      }

      const { error } = await authHelpers.supabase.auth.updateUser({
        data: {
          first_name: editingData.firstName,
          last_name: editingData.lastName,
          state_license_number: editingData.stateLicenseNumber
        }
      });

      if (error) {
        throw error;
      }

      if (newPin) {
        const { error: pinError } = await authHelpers.supabase.rpc('set_own_pin', { new_pin: newPin });
        if (pinError) {
          throw pinError;
        }
      }

      setUserInfo(prev => ({
        ...prev,
        firstName: editingData.firstName,
        lastName: editingData.lastName,
        stateLicenseNumber: editingData.stateLicenseNumber,
        pinResetRequired: newPin ? false : prev.pinResetRequired
      }));
      setEditingData(prev => ({ ...prev, pin: '' }));

      setIsEditing(false);
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error?.message ? `Failed to update profile: ${error.message}` : 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEmailChange = async () => {
    if (!emailChangeData.newEmail.trim()) return;

    setEmailChangeData(prev => ({
      ...prev,
      isProcessing: true,
      status: '',
      message: ''
    }));

    try {
      const { authHelpers } = await import('./global/supabase');

      const { data, error } = await authHelpers.supabase.auth.updateUser({
        email: emailChangeData.newEmail
      });

      if (error) {
        throw error;
      }

      setEmailChangeData(prev => ({
        ...prev,
        isProcessing: false,
        pendingEmail: prev.newEmail,
        status: 'sent',
        message: `Confirmation email sent to ${prev.newEmail}`,
        newEmail: ''
      }));
    } catch (error) {
      console.error('Email change error:', error);
      setEmailChangeData(prev => ({
        ...prev,
        isProcessing: false,
        status: 'error',
        message: error.message || 'Failed to send confirmation email. Please try again.'
      }));
    }
  };

  const handleResendEmail = async () => {
    setEmailChangeData(prev => ({
      ...prev,
      newEmail: prev.pendingEmail
    }));
    await handleEmailChange();
  };

  const handleChangeEmailAddress = () => {
    setEmailChangeData(prev => ({
      ...prev,
      status: '',
      message: '',
      pendingEmail: '',
      newEmail: ''
    }));
  };

  const handlePasswordChange = async () => {
    if (!passwordChangeData.newPassword.trim()) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'New password is required.' }));
      return;
    }
    if (passwordChangeData.newPassword.length < 6) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'Password must be at least 6 characters.' }));
      return;
    }
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      setPasswordChangeData(prev => ({ ...prev, status: 'error', message: 'Passwords do not match.' }));
      return;
    }
    setPasswordChangeData(prev => ({ ...prev, isProcessing: true, status: '', message: '' }));
    try {
      const { authHelpers } = await import('./global/supabase');
      const { error } = await authHelpers.supabase.auth.updateUser({ password: passwordChangeData.newPassword });
      if (error) throw error;
      setPasswordChangeData({ currentPassword: '', newPassword: '', confirmPassword: '', isProcessing: false, status: 'success', message: 'Password updated successfully!', showCurrentPassword: false, showNewPassword: false });
    } catch (error) {
      setPasswordChangeData(prev => ({ ...prev, isProcessing: false, status: 'error', message: error.message || 'Failed to update password.' }));
    }
  };

  return (
    <div className="container-center">
      {/* Hamburger Menu */}
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="card card-center" style={{ maxWidth: '500px', width: '100%' }}>
        <h1 className="title">Profile Settings</h1>

        {/* Subtle App Install Link - Mobile Only */}
        {isMobile && (
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <button
              onClick={() => setShowPWAPrompt(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                fontSize: '0.9em',
                textDecoration: 'underline',
                cursor: 'pointer',
                padding: '5px'
              }}
            >
              Install as App
            </button>
          </div>
        )}

        {/* Basic Profile Information */}
        <div style={{ marginBottom: '30px', width: '100%' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Personal Information</h3>

          {!isEditing ? (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <strong>First Name:</strong> {userInfo?.firstName || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Last Name:</strong> {userInfo?.lastName || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>State License Number:</strong> {userInfo?.stateLicenseNumber || 'Not set'}
              </div>
              <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <strong>PIN:</strong>
                <span>{userInfo?.pinResetRequired ? 'Reset required' : '••••••'}</span>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <strong>Editor:</strong> {userInfo?.isEditor ? 'Yes' : 'No'}
              </div>
              <div style={{ marginBottom: '20px' }}>
                <strong>Admin:</strong> {isAdmin ? 'Yes' : 'No'}
              </div>
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-primary"
                style={{ marginRight: '10px' }}
              >
                Edit Profile
              </button>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  First Name:
                </label>
                <input
                  type="text"
                  value={editingData.firstName}
                  onChange={(e) => setEditingData(prev => ({ ...prev, firstName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Last Name:
                </label>
                <input
                  type="text"
                  value={editingData.lastName}
                  onChange={(e) => setEditingData(prev => ({ ...prev, lastName: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  State License Number:
                </label>
                <input
                  type="text"
                  value={editingData.stateLicenseNumber}
                  onChange={(e) => setEditingData(prev => ({ ...prev, stateLicenseNumber: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  New PIN (6 digits, leave blank to keep current):
                </label>
                <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                  <input
                    type={showPin ? "text" : "password"}
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="new-password"
                    value={editingData.pin}
                    onChange={(e) => setEditingData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                    style={{
                      width: '100%',
                      padding: '8px 40px 8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      fontSize: '16px',
                      color: '#666'
                    }}
                    title={showPin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {showPin ? '●' : '○'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSaveProfile}
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingData({
                      firstName: userInfo?.firstName || '',
                      lastName: userInfo?.lastName || '',
                      stateLicenseNumber: userInfo?.stateLicenseNumber || '',
                      pin: ''
                    });
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Email Change Section */}
        <div style={{ marginBottom: '30px', width: '100%', borderTop: '1px solid #eee', paddingTop: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Email Address</h3>

          <div style={{ marginBottom: '20px' }}>
            <strong>Current Email:</strong> {userInfo?.email || 'No email available'}
            {emailChangeData.status === 'success' && (
              <span style={{ color: 'green', marginLeft: '10px' }}>Updated successfully</span>
            )}
          </div>

          {emailChangeData.status === 'sent' && emailChangeData.pendingEmail ? (
            <div style={{
              backgroundColor: '#e3f2fd',
              padding: '15px',
              borderRadius: '4px',
              marginBottom: '15px',
              border: '1px solid #bbdefb'
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>New Email:</strong> {emailChangeData.pendingEmail}
              </div>
              <div style={{ marginBottom: '10px', color: '#1976d2' }}>
                Status: Waiting for verification...
              </div>
              <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                Confirmation email sent to {emailChangeData.pendingEmail}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleResendEmail}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Resend to same email
                </button>
                <button
                  onClick={handleChangeEmailAddress}
                  className="btn btn-secondary"
                  style={{ fontSize: '12px', padding: '6px 12px' }}
                >
                  Change email address
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                New Email Address:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <input
                  type="email"
                  value={emailChangeData.newEmail}
                  onChange={(e) => setEmailChangeData(prev => ({ ...prev, newEmail: e.target.value }))}
                  placeholder="Enter new email address"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <button
                  onClick={handleEmailChange}
                  className="btn btn-primary"
                  disabled={emailChangeData.isProcessing || !emailChangeData.newEmail.trim()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {emailChangeData.isProcessing ? 'Sending...' : 'Send Confirmation'}
                </button>
              </div>

              {emailChangeData.status === 'error' && (
                <div style={{
                  color: '#d32f2f',
                  fontSize: '14px',
                  marginTop: '5px'
                }}>
                  {emailChangeData.message}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Password Change Section */}
        <div style={{ marginBottom: '30px', width: '100%', borderTop: '1px solid #eee', paddingTop: '30px' }}>
          <h3 style={{ marginBottom: '20px', color: '#333' }}>Change Password</h3>
          {passwordChangeData.status === 'success' && (
            <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '15px', borderRadius: '4px', marginBottom: '15px', border: '1px solid #c3e6cb' }}>
              {passwordChangeData.message}
            </div>
          )}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>New Password:</label>
            <input type={passwordChangeData.showNewPassword ? "text" : "password"} value={passwordChangeData.newPassword} onChange={(e) => setPasswordChangeData(prev => ({ ...prev, newPassword: e.target.value, status: '', message: '' }))} placeholder="Enter new password" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Confirm Password:</label>
            <input type="password" value={passwordChangeData.confirmPassword} onChange={(e) => setPasswordChangeData(prev => ({ ...prev, confirmPassword: e.target.value, status: '', message: '' }))} placeholder="Confirm new password" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }} />
          </div>
          {passwordChangeData.status === 'error' && (
            <div style={{ color: '#d32f2f', fontSize: '14px', marginBottom: '15px' }}>{passwordChangeData.message}</div>
          )}
          <button onClick={handlePasswordChange} className="btn btn-primary" disabled={passwordChangeData.isProcessing || !passwordChangeData.newPassword.trim() || !passwordChangeData.confirmPassword.trim()}>
            {passwordChangeData.isProcessing ? 'Updating...' : 'Change Password'}
          </button>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate('/home')}
          className="btn btn-secondary btn-full"
        >
          Back to Home
        </button>
      </div>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        isOpen={showPWAPrompt}
        onClose={() => setShowPWAPrompt(false)}
      />
    </div>
  );
}

// Main App Content Component (handles authentication and routing)
function AppContent() {
  const [userEmail, setUserEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check session and onboarding status on app load ONLY
  useEffect(() => {
    const initializeApp = async () => {
      // Check if URL contains password reset token
      const urlParams = new URLSearchParams(window.location.search);
      const tokenHash = urlParams.get('token_hash');
      const type = urlParams.get('type');

      if (tokenHash && type === 'recovery') {
        navigate(`/password-reset?token_hash=${tokenHash}&type=${type}`);
        setIsLoading(false);
        return;
      }

      // Check if user has completed onboarding
      if (!authHelpers.hasCompletedOnboarding()) {
        navigate('/onboarding');
        setIsLoading(false);
        return;
      }

      // Check if user has valid session
      const { data: session, error } = await authHelpers.checkValidSession();

      if (session && !error) {
        // User is logged in and session is valid
        console.log('Valid session found, going to home');

        // Get user details from PUBLIC.USERS table (not auth metadata)
        const user = session.user;
        if (user) {
          // Fetch from users table
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select(USER_PROFILE_COLUMNS)
            .eq('id', user.id)
            .single();

          if (userData && !userError) {
            setUserInfo({
              id: user.id,
              email: user.email,
              firstName: userData.first_name || 'User',
              lastName: userData.last_name || '',
              stateLicenseNumber: userData.state_license_number || '',
              pinResetRequired: userData.pin_reset_required === true,
              isEditor: userData.is_editor || false
            });
            setIsAdmin(userData.is_admin || false);
          } else {
            // Fallback to metadata if users table fails
            setUserInfo({
              id: user.id,
              email: user.email,
              firstName: user.user_metadata?.firstName || user.user_metadata?.first_name || 'User',
              lastName: user.user_metadata?.lastName || user.user_metadata?.last_name || '',
              stateLicenseNumber: user.user_metadata?.state_license_number || '',
              // Profile unreadable: fail closed so the PIN reset is not skipped.
              pinResetRequired: true,
              isEditor: false
            });
            setIsAdmin(false);
          }
        }

        // Only redirect to home if user is on root path - allow access to other auth routes
        if (location.pathname === '/') {
          navigate('/home');
        }
      } else {
        // No valid session - only redirect to login if not already on an auth route
        const authRoutes = ['/login', '/signup', '/forgot-password', '/password-reset', '/confirmation', '/onboarding'];
        if (!authRoutes.includes(location.pathname)) {
          console.log('No valid session, going to login');
          navigate('/login');
        }
      }

      setIsLoading(false);
    };

    initializeApp();
  }, [navigate, location.pathname]);

  // Separate useEffect for activity tracking
  useEffect(() => {
    // Build authenticated routes dynamically from config
    const staticAuthRoutes = ['/home', SET_PIN_PATH, '/daily-dashboard', '/admin-dashboard', '/profile',
      '/chain-of-custody-report', '/provider-usage-report', '/users'];
    const locationRoutes = LOCATIONS.map(loc => `/${loc.id}`);
    const authenticatedRoutes = [...staticAuthRoutes, ...locationRoutes];

    if (!authenticatedRoutes.includes(location.pathname)) return;

    // Set up activity tracking interval only when on authenticated screens
    const activityInterval = setInterval(() => {
      // Check if session timed out
      if (authHelpers.checkSessionTimeout()) {
        console.log('Session timed out, logging out');
        handleLogout();
      } else {
        // Update activity timestamp
        authHelpers.setLastActivity();
      }
    }, 60000); // Check every minute

    return () => clearInterval(activityInterval);
  }, [location.pathname]);

  const handleOnboardingComplete = () => {
    navigate('/login');
  };

  const handleSignupSuccess = (userData) => {
    console.log('Signup successful:', userData);
    setUserEmail(userData.user?.email || '');
    navigate('/confirmation');
  };

  const handleEmailConfirmed = () => {
    console.log('Email confirmed');
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleSwitchToLogin = () => {
    navigate('/login');
  };

  const handleSwitchToSignup = () => {
    navigate('/signup');
  };

  const handleLoginSuccess = async (userData) => {
    console.log('Login successful:', userData);
    // Set activity timestamp
    authHelpers.setLastActivity();

    // Extract user info from PUBLIC.USERS table
    const user = userData.user;
    if (user) {
      const { data: userRecord, error } = await supabase
        .from('users')
        .select(USER_PROFILE_COLUMNS)
        .eq('id', user.id)
        .single();

      if (userRecord && !error) {
        setUserInfo({
          id: user.id,
          email: user.email,
          firstName: userRecord.first_name || 'User',
          lastName: userRecord.last_name || '',
          isEditor: userRecord.is_editor || false,
          stateLicenseNumber: userRecord.state_license_number || '',
          pinResetRequired: userRecord.pin_reset_required === true
        });
        setIsAdmin(userRecord.is_admin || false);
      } else {
        // Fallback
        setUserInfo({
          id: user.id,
          email: user.email,
          firstName: 'User',
          lastName: '',
          isEditor: false,
          stateLicenseNumber: '',
          pinResetRequired: true
        });
        setIsAdmin(false);
      }
    }

    navigate('/home');
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handlePasswordResetRequest = (email) => {
    setUserEmail(email);
    navigate('/password-reset');
  };

  const handlePasswordResetComplete = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    try {
      await authHelpers.signOut();
      navigate('/login');
      setUserEmail('');
      setIsAdmin(false);
      setUserInfo(null);
    } catch (error) {
      console.log('Logout error:', error);
      navigate('/login');
    }
  };

  const handleNavigate = (destination) => {
    console.log('Navigate to:', destination);
    // Update activity on navigation
    authHelpers.setLastActivity();

    // Static routes
    const staticRoutes = {
      'home': '/home',
      'daily-dashboard': '/daily-dashboard',
      'profile': '/profile',
      'admin-dashboard': '/admin-dashboard',
      'chain-of-custody-report': '/chain-of-custody-report',
      'provider-usage-report': '/provider-usage-report',
      'users': '/users'
    };

    // Check static routes first
    if (staticRoutes[destination]) {
      navigate(staticRoutes[destination]);
      return;
    }

    // Check dynamic location routes from config
    const location = LOCATIONS.find(loc => loc.id === destination);
    if (location) {
      navigate(`/${location.id}`);
      return;
    }

    console.log('Unknown destination:', destination);
  };

  // Show loading while determining initial screen
  if (isLoading) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h2 className="title-auth">Loading...</h2>
        </div>
      </div>
    );
  }

  // A pending witness-PIN reset blocks every other screen.
  if (userInfo?.pinResetRequired && location.pathname !== SET_PIN_PATH) {
    return <Navigate to={SET_PIN_PATH} replace />;
  }

  return (
    <Routes>
      <Route path={SET_PIN_PATH} element={
        userInfo ? (
          <SetPin
            userInfo={userInfo}
            onComplete={() => {
              setUserInfo(prev => ({ ...prev, pinResetRequired: false }));
              navigate('/home');
            }}
            onLogout={handleLogout}
          />
        ) : <Navigate to="/login" replace />
      } />

      {/* Onboarding Routes */}
      <Route path="/onboarding" element={<Welcome onComplete={handleOnboardingComplete} />} />
      <Route path="/signup" element={
        <Signup
          onSignupSuccess={handleSignupSuccess}
          onSwitchToLogin={handleSwitchToLogin}
        />
      } />
      <Route path="/confirmation" element={
        <EmailConfirmation
          userEmail={userEmail}
          onConfirmed={handleEmailConfirmed}
          onGoToLogin={handleGoToLogin}
        />
      } />
      <Route path="/login" element={
        <Login
          onLoginSuccess={handleLoginSuccess}
          onSwitchToSignup={handleSwitchToSignup}
          onForgotPassword={handleForgotPassword}
        />
      } />
      <Route path="/forgot-password" element={
        <ForgotPassword
          onResetRequest={handlePasswordResetRequest}
          onBackToLogin={handleGoToLogin}
        />
      } />
      <Route path="/password-reset" element={
        <PasswordReset
          userEmail={userEmail}
          onResetComplete={handlePasswordResetComplete}
        />
      } />

      {/* Authenticated Routes */}
      <Route path="/home" element={
        <DashboardHome
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      <Route path="/profile" element={
        <Profile
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      {/* Dashboard Routes */}
      <Route path="/daily-dashboard" element={
        <DailyDashboard
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      <Route path="/admin-dashboard" element={
        <Admin
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      {/* Admin-only Routes */}
      <Route path="/chain-of-custody-report" element={
        <ChainOfCustodyReport
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      <Route path="/provider-usage-report" element={
        <ProviderUsageReport
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      <Route path="/users" element={
        <Users
          userInfo={userInfo}
          isAdmin={isAdmin}
          onLogout={handleLogout}
          onNavigate={handleNavigate}
        />
      } />

      {/* Dynamic Location Routes - Generated from config.js */}
      {LOCATIONS.map(loc => (
        <Route
          key={loc.id}
          path={`/${loc.id}`}
          element={
            <LocationInventory
              locationConfig={loc}
              userInfo={userInfo}
              isAdmin={isAdmin}
              onLogout={handleLogout}
              onNavigate={handleNavigate}
            />
          }
        />
      ))}

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

// Main App Component with Router wrapper
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;